import React, { useMemo } from 'react';
import TableWindow from '../../../../components/Table/TableWindow';
import getColumns from './WaterServices.columns';
import useTablePages from '../../../../hooks/useTablePages';
import { useOrderBy } from '../../../../hooks/useOrderBy';
import PageBar from '../../../../components/PageBar/PageBar';
import {
  GET_WATER_SERVICE_IDS,
  GET_WATER_SERVICES,
  WaterServiceItem,
} from './WaterServices.query';
import { RouteComponentProps, withRouter } from 'react-router';
import ThirdPaneLayout, {
  MainColumn,
} from '../../../../components/Layout/ThirdPaneLayout';
import useThirdPaneCallbacks from '../../../../hooks/useThirdPaneCallbacks';
import WaterServiceThirdPane from '../../../../components/WaterService/WaterServiceThirdPane';
import FilterBarWaterServices, {
  defaultFilters,
  WaterServiceFilters,
} from './FilterBarWaterServices';
import useMode from '../../../../hooks/useMode';
import useSessionInfo from '../../../../hooks/useSessionInfo';
import useColumnConfiguration from '../../../../hooks/useColumnConfiguration';
import useStoredFilters from '../../../../hooks/useStoredFilters';
import WaterServicesActions from './WaterServicesActions';
import SelectableMap, {
  MapPoint,
} from '../../../../components/Map/SelectableMap';
import useRightPaneConfiguration from '../../../../hooks/useRightPaneConfiguration';
import { getStringSearchOr } from '../../../../helpers/search';

type WaterServiceDetailParams = {
  id?: string;
};

type WaterServiceDetailProps = RouteComponentProps<WaterServiceDetailParams>;

export const searchFields = [
  {
    field: 'siteNumber',
    label: 'Service',
  },
  {
    field: 'meterNumber',
    label: 'Meter',
  },
  {
    field: 'address.street1',
    label: 'Address',
  },
  {
    field: 'address.street2',
    label: null,
  },
  {
    field: 'pin',
    label: 'TRAC',
  },
  {
    field: 'tags.text',
    label: 'Tags',
  },
  {
    field: 'serviceAddresses.customer.profile.name',
    label: 'Customer',
  },
];

const convertStringSearch = (stringSearch?: string) => ({
  or: getStringSearchOr(searchFields, stringSearch),
});

export const getWaterServiceSearchFilter = convertStringSearch;

const convertFilters = (storedFilters: WaterServiceFilters) => {
  return {
    status: storedFilters.status,
    type: storedFilters.type ? storedFilters.type : undefined,
    hazardProtectionStatus: storedFilters.hazardProtectionStatus
      ? storedFilters.hazardProtectionStatus
      : undefined,
    hasBackflowAssemblies:
      storedFilters.hasBackflowAssemblies !== undefined
        ? storedFilters.hasBackflowAssemblies
        : undefined,
    ...convertStringSearch(storedFilters.stringSearch),
    ...(storedFilters.tags && storedFilters.tags.length > 0
      ? { 'tags.text': storedFilters.tags }
      : {}),
  };
};

/**
 * @component for water services page
 */
function WaterServices({ match }: WaterServiceDetailProps): JSX.Element {
  const { features } = useSessionInfo();
  const hasWaterHazards = features.includes('WATER_HAZARDS');
  const availableColumns = getColumns(hasWaterHazards);

  const { storedFilters, setStoredFilters, queryFilters } =
    useStoredFilters<WaterServiceFilters>(
      convertFilters,
      'waterServices',
      defaultFilters
    );

  const { orderBy, onHeaderClick } = useOrderBy(
    'waterServices',
    availableColumns
  );

  const { rightPaneConfig, setRightPaneConfig } =
    useRightPaneConfiguration('waterServices');

  const {
    loading,
    error,
    itemIds,
    idItems,
    selectedIds,
    setSelectedIds,
    itemLoader,
  } = useTablePages<
    WaterServiceItem,
    'id',
    { id: string; address?: { latitude?: number; longitude?: number } }
  >(
    'waterServices',
    GET_WATER_SERVICE_IDS,
    {
      where: queryFilters,
      limit: null,
      orderBy,
      includeAddress: rightPaneConfig.visible,
    },
    GET_WATER_SERVICES,
    (ids) => ({
      where: {
        id: ids,
      },
      limit: null,
      orderBy,
    })
  );

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(availableColumns, 'waterServices');

  const { closeThirdPane, onRowClick, prev, prevEnabled, next, nextEnabled } =
    useThirdPaneCallbacks(
      `/${useMode()}/water-services`,
      itemIds,
      match.params.id
    );

  const points: MapPoint[] = useMemo(() => {
    return rightPaneConfig.visible
      ? idItems
          .filter(
            (item) =>
              typeof item.address?.longitude === 'number' &&
              typeof item.address?.latitude === 'number'
          )
          .map((item) => ({
            id: item.id,
            coordinates: [
              item.address?.longitude as number,
              item.address?.latitude as number,
            ],
          }))
      : [];
  }, [idItems]);

  return (
    <ThirdPaneLayout paneVisible={!!match.params.id}>
      <MainColumn px="mainContent" pb="mainContent">
        <PageBar title="Water Services" />
        <FilterBarWaterServices
          filters={storedFilters}
          setFilters={setStoredFilters}
          selectedId={match.params.id}
        />
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <TableWindow
            emptyMessage="No water services found."
            columns={visibleColumns}
            itemIds={itemIds}
            itemLoader={itemLoader}
            onSelectionChange={setSelectedIds}
            selectedIds={selectedIds}
            actionItems={<WaterServicesActions ids={selectedIds} />}
            activeIds={match.params.id ? [match.params.id] : undefined}
            onHeaderClick={onHeaderClick}
            orderBy={orderBy}
            onRowClick={onRowClick}
            columnConfiguration={columnConfiguration}
            setColumnConfiguration={setColumnConfiguration}
            rightPane={
              <SelectableMap
                points={points}
                onPointClick={onRowClick}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                flexBasis={rightPaneConfig.width}
              />
            }
            rightPaneConfig={rightPaneConfig}
            setRightPaneConfig={setRightPaneConfig}
            rightPaneIcon="map"
          />
        )}
      </MainColumn>
      <WaterServiceThirdPane
        waterServiceId={match.params.id}
        onPrev={prev}
        prevEnabled={prevEnabled}
        onNext={next}
        nextEnabled={nextEnabled}
        onToggle={closeThirdPane}
      />
    </ThirdPaneLayout>
  );
}

export default withRouter(WaterServices);
